import React from 'react';
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { Wallet, Coins, TrendingUp, Vault, ArrowUpRight, ArrowDownLeft, Droplet } from 'lucide-react';
import { useVault } from '../context/VaultContext';
import { usePalmentWallet } from '../context/DemoWalletContext';
import { fmtFull, fmtNum, timeAgo } from '../lib/format';


export default function Dashboard() {
  const { connected } = usePalmentWallet();
  const { pusdBalance, ypusdBalance, tvl, apy, history, txs, exchangeRate } = useVault();

  
  const walletValue = pusdBalance + ypusdBalance / exchangeRate;

  // Protocol-wide metrics are only meaningful in demo mode. Outside demo
  // mode we display $0 / —% so nobody mistakes the numbers for on-chain data.
  

  return (
    <section id="stats" className="relative py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-[11px] tracking-[0.26em] uppercase text-palm-mint/70">
              Live protocol metrics
            </div>
            <h2 className="mt-2 font-display font-bold tracking-tight text-3xl md:text-4xl">
              Your position,{' '}
              <span className="text-gradient">in motion.</span>
            </h2>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs text-white/50">
            <span className="w-1.5 h-1.5 rounded-full bg-palm-lime animate-pulseSlow" />
            Updated in real time
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          <StatCard
            icon={<Coins size={16} strokeWidth={1.6} />}
            label="yPUSD Balance"
            value={fmtFull(ypusdBalance, 4)}
            unit="yPUSD"
            sub={`≈ ${fmtFull((ypusdBalance / exchangeRate), 2)} PUSD`}
            accent
            testId="ypusd-balance"
          />
          <StatCard
            icon={<Wallet size={16} strokeWidth={1.6} />}
            label="PUSD Balance"
            value={fmtFull(pusdBalance, 2)}
            unit="PUSD"
            sub="Wallet available"
            testId="pusd-balance"
          />
          <StatCard
            icon={<TrendingUp size={16} strokeWidth={1.6} />}
            label="Current APY"
            value={`${apy}`}
            unit="%"
            sub="Compounding on-chain"
            testId="current-apy"
          />
          <StatCard
            icon={<Vault size={16} strokeWidth={1.6} />}
            label="Total Value Locked"
            value={fmtNum(tvl, 2)}
            unit="PUSD"
            sub={`${fmtFull(tvl / 1_000_000, 2)}M aggregate`}
            testId="tvl"
          />
        </div>

        {/* Charts + Activity */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 glass rounded-3xl p-6 md:p-8" data-testid="tvl-chart">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-palm-mint/70">
                  TVL · 30 day
                </div>
                <div className="mt-1 font-display font-bold text-2xl text-white">
                  ${fmtNum(tvl, 2)}
                </div>
              </div>
              <div className="glass rounded-full px-3 py-1.5 text-[11px] tracking-widest uppercase text-palm-mint/80">
                Solana Devnet
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <AreaChart data={history} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="tvlGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#E3EF26" stopOpacity={0.55} />
                      <stop offset="100%" stopColor="#076653" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(226,251,206,0.06)" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={60}
                    tickFormatter={(v) => `${(v / 1_000_000).toFixed(1)}M`}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'rgba(12,52,44,0.92)',
                      border: '1px solid rgba(227,239,38,0.35)',
                      borderRadius: 12,
                      color: '#E2FBCE',
                      fontFamily: 'Manrope',
                    }}
                    labelStyle={{ color: 'rgba(226,251,206,0.7)' }}
                    formatter={(v) => [`$${Number(v).toLocaleString()}`, 'TVL']}
                  />
                  <Area
                    type="monotone"
                    dataKey="tvl"
                    stroke="#E3EF26"
                    strokeWidth={2}
                    fill="url(#tvlGrad)"
                    activeDot={{ r: 5, fill: '#E3EF26', stroke: '#0C342C', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="glass rounded-3xl p-6 md:p-8 flex flex-col" data-testid="activity-feed">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-palm-mint/70">
                  Activity
                </div>
                <div className="mt-1 font-display font-bold text-lg">Recent transactions</div>
              </div>
              <div className="glass rounded-full px-2.5 py-1 text-[10px] tracking-widest uppercase text-palm-mint/70">
                {txs.length}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto max-h-[340px] pr-1 -mr-1 space-y-3">
              {!connected ? (
                <Empty
                  title="Wallet not connected"
                  body="Connect your wallet to see your transactions."
                />
              ) : txs.length === 0 ? (
                <Empty
                  title="No activity yet"
                  body="Claim faucet PUSD, then deposit to mint your first yPUSD."
                />
              ) : (
                txs.map((tx) => <TxRow key={tx.id} tx={tx} />)
              )}
            </div>

            <div className="dashed my-5" />
            <div className="flex items-center justify-between text-xs text-white/50">
              <span>Your net worth</span>
              <span className="text-white/90 font-semibold">
                {fmtFull(walletValue, 2)} PUSD
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatCard({ icon, label, value, unit, sub, accent, testId }) {
  return (
    <div
      className={`relative glass rounded-3xl p-6 hover:-translate-y-1 transition-all duration-300 overflow-hidden ${
        accent ? 'border-palm-lime/25' : ''
      }`}
      data-testid={testId}
    >
      {accent && (
        <div
          aria-hidden
          className="absolute -top-20 -right-20 w-56 h-56 rounded-full blur-3xl opacity-40"
          style={{ background: 'radial-gradient(circle, #E3EF26, transparent 60%)' }}
        />
      )}
      <div className="relative flex items-start justify-between">
        <div className="w-9 h-9 rounded-xl glass flex items-center justify-center text-palm-lime">
          {icon}
        </div>
      </div>
      <div className="relative mt-5 text-[11px] tracking-[0.22em] uppercase text-palm-mint/70">
        {label}
      </div>
      <div className="relative mt-2 flex items-baseline gap-1.5">
        <span className="font-display font-black text-3xl tracking-tight text-white">
          {value}
        </span>
        <span className="text-xs font-semibold text-white/60">{unit}</span>
      </div>
      <div className="relative mt-2 text-xs text-white/50">{sub}</div>
    </div>
  );
}

function TxRow({ tx }) {
  const cfg = {
    deposit: { icon: <ArrowDownLeft size={14} strokeWidth={1.8} />, label: 'Deposit', tone: 'text-palm-lime' },
    withdraw: { icon: <ArrowUpRight size={14} strokeWidth={1.8} />, label: 'Withdraw', tone: 'text-palm-mint' },
    faucet: { icon: <Droplet size={14} strokeWidth={1.8} />, label: 'Faucet', tone: 'text-palm-mint' },
  };
  const c = cfg[tx.kind] || cfg.deposit;
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl bg-black/20 border border-white/5 hover:border-palm-mint/20 transition-all"
      data-testid={`tx-row-${tx.kind}`}
    >
      <div className={`w-8 h-8 rounded-lg glass flex items-center justify-center ${c.tone}`}>
        {c.icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-white/95">{c.label}</span>
          <span className="text-[10px] tracking-widest uppercase text-white/40">
            {timeAgo(tx.time)}
          </span>
        </div>
        <div className="mt-0.5 text-xs text-white/55 truncate">
          {tx.kind === 'faucet' && `+${fmtFull(tx.pusd, 0)} PUSD`}
          {tx.kind === 'deposit' &&
            `${fmtFull(tx.pusd, 2)} PUSD → ${fmtFull(tx.ypusd, 4)} yPUSD`}
          {tx.kind === 'withdraw' &&
            `${fmtFull(tx.ypusd, 4)} yPUSD → ${fmtFull(tx.pusd, 2)} PUSD`}
        </div>
      </div>
    </div>
  );
}

function Empty({ title, body }) {
  return (
    <div className="h-full min-h-[180px] flex flex-col items-center justify-center text-center py-8">
      <div className="w-10 h-10 rounded-xl glass flex items-center justify-center text-palm-mint/70 mb-3">
        <Vault size={16} strokeWidth={1.5} />
      </div>
      <div className="text-sm font-semibold text-white/90">{title}</div>
      <div className="mt-1 text-xs text-white/50 max-w-[200px]">{body}</div>
    </div>
  );
}
